import React, { useEffect, useRef } from 'react';
import { Trans } from 'react-i18next';

import PureList from '@common/components/list';
import Icon from '@common/components/icon';
import ScreenComponent from './component';
import { PerformanceFormComponent } from '@modules/performance/types';

export const EScales = {
  small: 0.75,
  xsmall: 0.666666,
};

type OwnProps = {
  component?: PerformanceFormComponent;
  componentId: string;
  disableResolution: boolean;
  scrollRef: React.RefObject<HTMLDivElement>;
  resolution: { x: number; y: number };
  size?: 'small' | 'xsmall';
  highlight?: string;
  onOpenComponent?: (component: PerformanceFormComponent) => void;
  focusedComponentId?: string;
};

type Props = OwnProps;

export const ScreenPreview = ({
  scrollRef,
  component,
  componentId,
  size,
  disableResolution,
  resolution = { x: 375, y: 667 },
  highlight,
  onOpenComponent,
  focusedComponentId,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (component?.id === focusedComponentId && scrollRef.current && containerRef.current) {
      scrollRef.current.scroll({ top: containerRef.current.offsetTop, behavior: 'smooth' });
    }
  }, [focusedComponentId, componentId]);

  const scale = (size && EScales[size]) || 1;

  const classNames = ['Screen'];
  if (onOpenComponent) classNames.push('Screen--clickable');


  return (
    <div
      className={classNames.join(' ')}
      ref={containerRef}
      style={!disableResolution ? {
        width: resolution.x * scale,
        height: resolution.y * scale,
      } : undefined}
    >
      <div
        className="Screen__Inner"
        style={!disableResolution ? {
          width: resolution.x,
          height: resolution.y,
          marginLeft: -((resolution.x - (resolution.x * scale)) / 2),
          marginTop: -((resolution.y - (resolution.y * scale)) / 2),
          transform: `scale(${scale}, ${scale})`,
          WebkitTransform: `scale(${scale}, ${scale})`,
        } : undefined}
      >
        <div className="Screen__Content">
          <PureList
            items={component ? [component] : []}
            renderRow={ScreenComponent}
            rowProps={(item) => ({
              onOpenComponent,
              highlight: item.id === highlight,
            })}
            placeholder={onOpenComponent && (
              <div className="Screen__Placeholder">
                <Icon type="dashboard" size="large" />
                <b><Trans i18nKey="learning:screen_preview_placeholder_title" /></b>
                <div>
                  <Trans i18nKey="learning:screen_preview_placeholder_description" />
                </div>
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default ScreenPreview;
